import { isFunction } from '@/global/utils/utils'

/**
 * A winter service request is a task someone asked to be carried out, so almost
 * everything the screens show about one belongs to the task behind it. The request
 * itself only decides which vehicle takes it and who drives - and whether either of
 * those can still be used, which is what the screens paint red.
 */

/**
 * Whether the request's vehicle can no longer take it. Reported as `is_blocked` off the
 * vehicle's availability status.
 */
export function hasBlockedVehicle (record) {
  return record?.vehicle?.is_blocked === true
}

/**
 * Whether the request's driver can no longer take it. Reported as `is_active`, which is
 * absent on older payloads - only an explicit `false` counts as a problem.
 */
export function hasInactiveDriver (record) {
  return record?.driver?.is_active === false
}

/**
 * Whether anything about the request stands in the way of a work order. The listing
 * tints such a row, and the details screen frames the offending value.
 */
export function isAbnormal (record) {
  return hasBlockedVehicle(record) || hasInactiveDriver(record)
}

/**
 * The label a codebook entry is shown under. The task endpoint reports these already
 * translated on `description`, falling back to the raw name.
 */
function codebookLabel (codebook) {
  return codebook?.description ?? codebook?.name ?? null
}

/**
 * The request's own values as the flat keys the fields are bound to.
 *
 * The backend already resolves the vehicle and the driver down from the task, so these
 * are the effective ones either way. Both of them are picked here - they are the two
 * values a request lets a base manager change.
 *
 * `translate` is what the status is resolved with: the request comes from the shared
 * incoming request endpoint, which hands its codebooks over as the translation keys
 * they are kept as - unlike the winter service task endpoint, which resolves its own
 * (see `codebookLabel`). Every other screen in the module translates that key itself.
 */
export function toRequestForm (request, translate) {
  const statusLabel = request?.status?.description

  return {
    id: request?.id ?? null,
    status: (statusLabel && isFunction(translate) ? translate(statusLabel) : statusLabel) ??
      request?.status?.name ?? null,
    vehicle_id: request?.vehicle?.id ?? null,
    driver_id: request?.driver?.id ?? null
  }
}

/**
 * Writes the flat keys onto the record itself. Content.vue hands `onDataReceived` the
 * very object it then turns into the form data, so the flat values have to land on it
 * rather than on a copy - that also keeps the untouched copy it restores on cancel flat,
 * instead of emptying every field.
 */
export function applyRequestForm (record, translate) {
  if (!record) {
    return record
  }

  Object.assign(record, toRequestForm(record, translate))
  // The request carries a `data` column of its own, and the tab layout reads a key of
  // exactly that name as "the record is nested one level down" - leaving it behind is
  // what makes the layout mistake the column for the record. Nothing on the screen
  // needs it: what a save has to keep of it is merged by the endpoint itself.
  delete record.data

  return record
}

/**
 * The same translation as a fresh object. Used after a save: the record the layout
 * already holds has been made reactive, so keys added to it afterwards would not be
 * picked up - it has to be handed a new object instead.
 */
export function withRequestForm (record, translate) {
  const { data: removed, ...rest } = record || {}

  return {
    ...rest,
    ...toRequestForm(record, translate)
  }
}

/**
 * The task behind the request -> the flat keys the fields are bound to.
 *
 * All of it is a label rather than an id: the screen only shows it, and what it shows has
 * to read the same as on the task's own screen.
 *
 * Everything the vehicle decides is read off the vehicle that actually takes the request,
 * which is the request's own whenever it overrode the task's - the request endpoint
 * reports it in the same shape the task endpoint does. Only a request that overrode
 * nothing falls back to the task's vehicle, and for one of those the two are the same
 * anyway.
 */
export function toTaskSnapshotForm (task, requestVehicle = null) {
  const vehicle = requestVehicle ?? task?.vehicle

  return {
    task_type: codebookLabel(task?.type),
    task_category: codebookLabel(task?.category),
    surface_type: codebookLabel(task?.surface_type),
    cleaning_type: codebookLabel(task?.cleaning_type),
    spread_type: codebookLabel(task?.spread_type),
    spread_quantity: task?.spread_quantity ?? null,
    worker_count: task?.worker_count ?? null,
    vehicle_type: codebookLabel(vehicle?.type),
    drive_type: codebookLabel(vehicle?.drive_type),
    throw_side: codebookLabel(vehicle?.throw_side),
    ownership: codebookLabel(vehicle?.ownership),
    base_name: vehicle?.base?.name ?? null,
    base_responsible_person: vehicle?.base?.responsible_person?.name ?? null,
    operator_name: vehicle?.operator?.name ?? null,
    subcontractor_name: vehicle?.subcontractor?.name ?? null
  }
}

/**
 * Form values -> what the update endpoint is sent.
 *
 * The vehicle and the driver are the only two values a request lets anyone change - the
 * rest belongs to the task it was made from - so those two, flat, are the whole payload.
 * Every other column stays as it is on the record.
 */
export function toRequestPayload (formData) {
  return {
    vehicle_id: formData?.vehicle_id ?? null,
    driver_id: formData?.driver_id ?? null
  }
}
